import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiError } from '../../Documents/format';
import { DEFAULT_PAGE_SIZE, DEFAULT_SORT, EMPTY_FILTERS } from '../constants';
import { isCancelled, markingApi } from '../markingApi';
import { MarkingFilters, MarkingPagination, MarkingRow, SortField, SortState } from '../types';

const SEARCH_DELAY_MS = 300;

/** Поля, по которым естественный порядок «от новых к старым» (первый клик по заголовку - по убыванию) */
const DESC_FIRST: SortField[] = ['date', 'document'];

/**
 * Список кодов маркировки: фильтры + сортировка + пагинация.
 *  - поиск применяется с задержкой, устаревшие запросы отменяются;
 *  - при перезагрузке прежние строки остаются на экране (без «мигания»);
 *  - страница нумеруется с 1 (как на сервере).
 */
export function useAdminMarkingCodes(enabled: boolean) {
  const [filters, setFilters] = useState<MarkingFilters>(EMPTY_FILTERS);
  const [searchApplied, setSearchApplied] = useState('');
  const [sort, setSort] = useState<SortState>(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [limit, setLimitState] = useState(DEFAULT_PAGE_SIZE);
  const [reloadToken, setReloadToken] = useState(0);

  const [rows, setRows] = useState<MarkingRow[]>([]);
  const [pagination, setPagination] = useState<MarkingPagination>({ page: 1, limit: DEFAULT_PAGE_SIZE, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  // Поиск: отложенное применение, чтобы не слать запрос на каждый символ
  useEffect(() => {
    if (filters.search === searchApplied) return undefined;
    const timer = window.setTimeout(() => {
      setSearchApplied(filters.search);
      setPage(1);
    }, SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [filters.search, searchApplied]);

  const effective = useMemo<MarkingFilters>(() => ({ ...filters, search: searchApplied }), [filters, searchApplied]);

  useEffect(() => {
    if (!enabled) return undefined;
    const current = ++seq.current;
    const controller = new AbortController();
    setLoading(true);

    markingApi
      .list(effective, sort, page, limit, controller.signal)
      .then(data => {
        if (current !== seq.current) return;
        setRows(data.codes);
        setPagination(data.pagination);
        setError(null);
      })
      .catch(err => {
        if (isCancelled(err) || current !== seq.current) return;
        setError(apiError(err, 'Не удалось загрузить коды маркировки'));
      })
      .finally(() => {
        if (current === seq.current) setLoading(false);
      });

    return () => controller.abort();
  }, [enabled, effective, sort, page, limit, reloadToken]);

  const patchFilters = useCallback((patch: Partial<MarkingFilters>) => {
    setFilters(prev => ({ ...prev, ...patch }));
    if (!('search' in patch)) setPage(1);
  }, []);

  const resetFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS);
    setSearchApplied('');
    setPage(1);
  }, []);

  const toggleSort = useCallback((key: SortField) => {
    setSort(prev => (prev.sortBy === key
      ? { sortBy: key, sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc' }
      : { sortBy: key, sortOrder: DESC_FIRST.indexOf(key) !== -1 ? 'desc' : 'asc' }));
    setPage(1);
  }, []);

  const setLimit = useCallback((value: number) => {
    setLimitState(value);
    setPage(1);
  }, []);

  const reload = useCallback(() => setReloadToken(t => t + 1), []);

  return {
    filters, effective, patchFilters, resetFilters,
    sort, toggleSort,
    page, setPage, limit, setLimit,
    rows, pagination, loading, error, reload
  };
}
